import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'react-qr-code';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const QRCodeGenerator = ({
  invoiceId,
  invoiceData,
  type = 'invoice',
  size = 96,
  className = '',
  showSpecifications = false,
  minimal = false
}) => {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateQRCode = useCallback(async () => {
    if (!invoiceId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.invoices.generateQRCode(invoiceId, { type });

      console.log('🔍 QR FRONTEND: Received QR data:', {
        qr_code: response.data.qr_code,
        qr_image: response.data.qr_image,
        data_length: response.data.qr_code?.length || 0
      });

      setQrData(response.data.qr_code);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      setError('Failed to generate QR code');
      toast.error('Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  }, [invoiceId, type]);

  const generateQRCodeFromData = useCallback(() => {
    if (!invoiceData) return;

    // Generate QR data locally with required fields
    const qrDataString = createQRDataString(invoiceData);
    setQrData(qrDataString);
  }, [invoiceData]);

  useEffect(() => {
    if (invoiceId) {
      generateQRCode();
    } else if (invoiceData) {
      generateQRCodeFromData();
    }
  }, [generateQRCode, generateQRCodeFromData, invoiceData, invoiceId]);

  const createQRDataString = (data) => {
    // SIMPLIFIED: Only FBR Submitted Invoice Number as per user requirement
    const fbrNumber = data.fbr_invoice_number || data.fbrInvoiceNumber || data.invoice_number || data.invoiceNumber || 'N/A';
    
    // Return only the FBR Invoice Number
    return fbrNumber;
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-red-50 border border-red-200 rounded ${className}`} 
           style={{ width: size, height: size }}>
        <span className="text-red-600 text-xs text-center p-2">QR Error</span>
      </div>
    );
  }

  if (!qrData) {
    return (
      <div className={`flex items-center justify-center bg-gray-50 border border-gray-200 rounded ${className}`} 
           style={{ width: size, height: size }}>
        <span className="text-gray-500 text-xs">No QR</span>
      </div>
    );
  }

  const renderQRCode = () => {
    try {
      // Check if qrData is a base64 data URL (image)
      if (qrData && qrData.startsWith('data:image/')) {
        console.log('🔍 QR FRONTEND: Displaying QR image directly');
        return (
          <img
            src={qrData}
            alt="QR Code"
            style={{
              width: size,
              height: size,
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain'
            }}
          />
        );
      }

      // Check if QR data is too long for text-based QR generation
      if (qrData && qrData.length > 2953) { // Maximum for QR Code version 40 with error correction level M
        console.warn('QR data too long:', qrData.length, 'characters');
        return (
          <div className="flex items-center justify-center bg-yellow-50 border border-yellow-200 rounded text-yellow-700 text-xs text-center p-2">
            QR data too large<br/>({qrData.length} chars)
          </div>
        );
      }

      // Generate QR code from text data
      return (
        <QRCode
          value={qrData}
          size={size}
          level="M"
          style={{
            height: 'auto',
            maxWidth: '100%',
            width: '100%'
          }}
        />
      );
    } catch (error) {
      console.error('QR Code rendering error:', error);
      return (
        <div className="flex items-center justify-center bg-red-50 border border-red-200 rounded text-red-600 text-xs text-center p-2">
          QR Error<br/>{error.message}
        </div>
      );
    }
  };

  return (
    <div className={`qr-code-container ${className}`}>
      <div
        id={`qr-code-${invoiceId || 'preview'}`}
        className={minimal ? "bg-white" : "bg-white p-1 border border-gray-300 rounded"}
        style={{ width: minimal ? size : size + 8, height: minimal ? size : size + 8 }}
      >
        {renderQRCode()}
      </div>
    </div>
  );
};

export default QRCodeGenerator;
